import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "headphones",
  style: {"max-height":"320px","align-items":"center","display":"flex","justify-content":"center"}
}
const _hoisted_2 = { style: {"color":"#737373","text-align":"center","font-weight":"500","font-size":"15px"} }

import { computed, ComputedRef, onMounted, Ref, ref, watch } from "vue";

import BasePage from "@/components/BasePage.vue";
import {
  useConfigureParticipantId,
  useOpenBCIUtils,
  useWebsocketConnection,
} from "@/utils/hooks";
import * as d3 from "d3";
import { NODES } from "@/utils/types";
import { BaseType } from "d3";
import {
  Node,
  Node2,
  NODES_DEFAULT_VALUES,
} from "@/pages/optimizeSignalAndImpedancePage/utils/optimizeSignalAndImpedanceTypes";
import { getSignalState } from "@/pages/optimizeSignalAndImpedancePage/utils/helpers";
import { SerialDataRMS } from "@/utils/openBCISerialTypes";
import {
  PhArrowRight,
  PhCheckCircle,
  PhWarningCircle,
} from "@phosphor-icons/vue";
import OptimizeSignalAside from "@/pages/optimizeSignalAndImpedancePage/aside/OptimizeSignalAside.vue";
import OptimizeSignalAudioAndImpedancePanel from "@/components/audioAndImpedancePanel/OptimizeSignalAudioAndImpedancePanel.vue";
import CHANNEL_ASSIGNMENT from "../../config/channelAssignment.json";
import { ROUTES } from "@/utils/routes";

// ---- STATE ----


export default /*@__PURE__*/_defineComponent({
  __name: 'OptimizeSignalAndImpedancePage',
  setup(__props) {

useConfigureParticipantId();
useWebsocketConnection();

const channelAssignment = computed(
  () => CHANNEL_ASSIGNMENT[channelConfig.value!],
);

const channelConfig = ref<string | null>("H");

const {
  startSignalQualityCheck,
  signalRMS,
  isImpedanceCheckRunning,
  impedanceCheckChannel,
  runImpedanceCheck,
  stopRecording,
} = useOpenBCIUtils();

const preventUnloadWarningDialog = ref(false);

const isAudioAndImpedancePanelOpen = ref(false);

const reactiveSignalRMS = ref<SerialDataRMS>(signalRMS.value);

const baseModel = ref(null);
const svg: Ref<d3.Selection<
  SVGImageElement,
  unknown,
  HTMLElement,
  any
> | null> = ref(null);
const tooltip: Ref<d3.Selection<BaseType, unknown, HTMLElement, any> | null> =
  ref(null);
const circles: Ref<d3.Selection<
  SVGCircleElement,
  { id: string; x: number; y: number; r: number },
  SVGImageElement,
  unknown
> | null> = ref(null);

const isOverAllSignalQualitySufficient = computed(
  () => !nodeData.value.some((node) => [0, 1, 2].includes(node.state)),
);

const nodeData: ComputedRef<Node[]> = computed(() => {
  //const a = reactiveSignalRMS.value;

  if (!reactiveSignalRMS.value) {
    return NODES_DEFAULT_VALUES;
  }

  // Wir betrachten von signalRMS nur A1 bis A8

  const nodes: Node[] = [];

  // console.log(reactiveSignalRMS.value);
  // console.log(reactiveSignalRMS.value["A1"]);
  // console.log(JSON.stringify(reactiveSignalRMS.value));

  for (const key in reactiveSignalRMS.value) {
    switch (key) {
      case "A1":
        nodes.push({
          node_id: channelAssignment.value.A1,
          state: getSignalState({ signal: reactiveSignalRMS.value.A1 ?? 0 }),
          impedance: reactiveSignalRMS.value.A1 ?? 0,
        });
        break;
      case "A2":
        nodes.push({
          node_id: channelAssignment.value.A2,
          state: getSignalState({ signal: reactiveSignalRMS.value.A2 ?? 0 }),
          impedance: reactiveSignalRMS.value.A2 ?? 0,
        });
        break;
      case "A3":
        nodes.push({
          node_id: channelAssignment.value.A3,
          state: getSignalState({ signal: reactiveSignalRMS.value.A3 ?? 0 }),
          impedance: reactiveSignalRMS.value.A3 ?? 0,
        });
        break;
      case "A4":
        nodes.push({
          node_id: channelAssignment.value.A4,
          state: getSignalState({ signal: reactiveSignalRMS.value.A4 ?? 0 }),
          impedance: signalRMS.value.A4 ?? 0,
        });
        break;
      case "A5":
        nodes.push({
          node_id: channelAssignment.value.A5,
          state: getSignalState({ signal: reactiveSignalRMS.value.A5 ?? 0 }),
          impedance: reactiveSignalRMS.value.A5 ?? 0,
        });
        break;
      case "A6":
        nodes.push({
          node_id: channelAssignment.value.A6,
          state: getSignalState({ signal: reactiveSignalRMS.value.A6 ?? 0 }),
          impedance: reactiveSignalRMS.value.A6 ?? 0,
        });
        break;
      case "A7":
        nodes.push({
          node_id: channelAssignment.value.A7,
          state: getSignalState({ signal: reactiveSignalRMS.value.A7 ?? 0 }),
          impedance: reactiveSignalRMS.value.A7 ?? 0,
        });
        break;
      case "A8":
        nodes.push({
          node_id: channelAssignment.value.A8,
          state: getSignalState({ signal: reactiveSignalRMS.value.A8 ?? 0 }),
          impedance: reactiveSignalRMS.value.A8 ?? 0,
        });
        break;
    }
  }

  return nodes;
});

watch(
  () => nodeData.value,
  () => updateCircles(),
);

// ---- LIFECYCLE HOOKS ----

onMounted(() => {
  window.addEventListener("beforeunload", handleBeforeUnload);

  const urlParams = new URLSearchParams(window.location.search);
  channelConfig.value = urlParams.get("wlmtdoqtqe");

  initializeD3();

  // Start the signal quality check in the background
  startSignalQualityCheck();
});

// ---- METHODS ----

const initializeD3 = () => {
  console.log("Initializing D3");
  svg.value = d3
    .select(baseModel.value)
    .append("image")
    .attr("xlink:href", require("@/assets/baseModel.png"))
    .attr("width", 1000)
    .attr("height", 500);
  tooltip.value = d3.select(".tooltip");

  svg.value = d3.select(baseModel.value);

  if (!svg.value) {
    return;
  }

  circles.value = svg.value
    .selectAll("circle")
    .data(NODES)
    .enter()
    .append("circle")
    .attr("id", (d: Node2) => `node-${d.id}`)
    .attr("cx", (d: Node2) => d.x)
    .attr("cy", (d: Node2) => d.y)
    .attr("r", (d: Node2) => d.r)
    .attr("class", "node");

  updateCircles();
};

const updateCircles = () => {
  if (!baseModel.value) {
    return;
  }

  svg.value = d3.select(baseModel.value);

  if (svg.value === null) {
    return;
  }

  svg.value
    .selectAll("circle")
    .data(nodeData.value, (d: any) => d.node_id)
    .join(
      (enter: any) =>
        enter
          .append("circle")
          .attr("class", (d: Node) => `node ${stateToClass(d.state)}`)
          .attr(
            "cx",
            (d: Node) => NODES.find((n) => n.id === d.node_id)?.x || 0,
          )
          .attr(
            "cy",
            (d: Node) => NODES.find((n) => n.id === d.node_id)?.y || 0,
          )
          .attr("r", 12)
          .on("mouseover", handleMouseOver)
          .on("mouseout", handleMouseOut),
      (update: any) =>
        update.attr("class", (d: any) => `node ${stateToClass(d.state)}`),
    );
};

const handleMouseOut = (event: MouseEvent) => {
  if (!tooltip.value || !svg.value) {
    return;
  }

  tooltip.value.style("opacity", 0);
  d3.select(event.target as SVGCircleElement).attr("r", 12); // Reset radius

  // Remove desaturation from all circles
  svg.value.selectAll("circle").classed("desaturated", false);
};

const handleMouseOver = (event: MouseEvent, d: Node) => {
  if (!tooltip.value || !svg.value) {
    return;
  }
  console.log(d);

  tooltip.value
    .style("opacity", 1)
    .html(
      `Node ID: ${d.node_id}<br/>State: ${stateToClass(
        d.state,
      )}<br/>µRMS: ${d.impedance}`,
    )
    .style("left", `${event.offsetX + 200}px`)
    .style("top", `${event.offsetY + 300}px`);

  // Select the current circle element using D3's event handling
  d3.select(event.target as SVGCircleElement).attr("r", 14); // Enlarge on hover

  // Desaturate other circles
  svg.value
    .selectAll("circle")
    .filter((node: any) => node.node_id !== d.node_id)
    .classed("desaturated", true);
};

// TODO - Move to utils; Make Array Constant
const stateToClass = (state: number) => {
  const stateClasses = ["off", "bad", "moderate", "better", "good"];
  return stateClasses[state] || "unknown";
};

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  const confirmationMessage =
    "Are you sure you want to leave? Changes you made may not be saved.";

  console.log(preventUnloadWarningDialog.value);
  event.returnValue = confirmationMessage; // Standard for most browsers
  if (!preventUnloadWarningDialog.value) {
    event.returnValue = confirmationMessage; // Standard for most browsers
  }
  return confirmationMessage; // For some browsers
};
const handleRedirectToRecording = async () => {
  isAudioAndImpedancePanelOpen.value = true;
};

return (_ctx: any,_cache: any) => {
  const _component_VCard = _resolveComponent("VCard")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_VRow = _resolveComponent("VRow")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BasePage, { heading: "Adjust Position" }, {
      default: _withCtx(() => [
        _createVNode(OptimizeSignalAside, { style: {"position":"absolute","top":"150px","left":"70px","width":"fit-content"} }),
        _cache[2] || (_cache[2] = _createElementVNode("p", { style: {"color":"#737373","text-align":"center","font-weight":"500","font-size":"18px"} }, [
          _createTextVNode(" Please adjust the headset until most of the electrodes "),
          _createElementVNode("br"),
          _createTextVNode("have a good connection, as indicated by the color in the picture. ")
        ], -1)),
        _createElementVNode("div", _hoisted_1, [
          (_openBlock(), _createElementBlock("svg", {
            ref_key: "baseModel",
            ref: baseModel,
            width: "1000",
            height: "500",
            style: {"position":"relative","right":"-11px","scale":"0.79","top":"15px","z-index":"900"}
          }, null, 512)),
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "tooltip" }, null, -1))
        ]),
        _createVNode(_component_VCard, {
          class: "rounded-pill d-flex justify-start align-center pl-3 pr-4 mb-8",
          style: {"height":"40px"},
          color: isOverAllSignalQualitySufficient.value ? '#dff5e0' : '#ffe7ea'
        }, {
          default: _withCtx(() => [
            (!isOverAllSignalQualitySufficient.value)
              ? (_openBlock(), _createBlock(_unref(PhWarningCircle), {
                  key: 0,
                  size: "24",
                  class: "mr-2",
                  color: "#E72321"
                }))
              : (_openBlock(), _createBlock(_unref(PhCheckCircle), {
                  key: 1,
                  size: "24",
                  class: "mr-2",
                  color: "#68a772"
                })),
            _createElementVNode("p", _hoisted_2, _toDisplayString(!isOverAllSignalQualitySufficient.value
            ? "Overall signal quality insufficient"
            : "You are good to go"), 1)
          ]),
          _: 1
        }, 8, ["color"]),
        _createVNode(_component_VRow, {
          class: "mb-2",
          style: {"gap":"16px","max-height":"60px"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_btn, {
              onClick: handleRedirectToRecording,
              "append-icon": _unref(PhArrowRight)
            }, {
              default: _withCtx(() => _cache[1] || (_cache[1] = [
                _createTextVNode(" Start Recording ")
              ])),
              _: 1
            }, 8, ["append-icon"])
          ]),
          _: 1
        }),
        _cache[3] || (_cache[3] = _createElementVNode("p", { style: {"font-size":"13px","color":"#939393","text-align":"center","max-width":"600px"} }, [
          _createTextVNode(" An impedance check of the electrodes will be performed. This may take a minute. "),
          _createElementVNode("br"),
          _createTextVNode("Afterwards, the recording is started and you can start with your task. ")
        ], -1))
      ]),
      _: 1
    }),
    (isAudioAndImpedancePanelOpen.value)
      ? (_openBlock(), _createBlock(OptimizeSignalAudioAndImpedancePanel, {
          key: 0,
          "is-setup": true,
          "impedance-check-channel": _unref(impedanceCheckChannel),
          "is-impedance-check-running": _unref(isImpedanceCheckRunning),
          "run-impedance-check": _unref(runImpedanceCheck),
          "stop-recording": _unref(stopRecording),
          "next-route": _unref(ROUTES).RECORDING,
          "impedance-panel-description": `Resistance measurements for each electrode.<br />You will be able to start your session after this step.`
        }, null, 8, ["impedance-check-channel", "is-impedance-check-running", "run-impedance-check", "stop-recording", "next-route"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})